import React, { useState } from 'react';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { Body1, Body5, Container, Page } from '@ui';
import CheckCircle from '@assets/icons/svgs/CheckCircle';
import { useNavigate } from 'react-router';
import { ROUTES } from '@constants/routes';
import { toCapitalCase } from '@utils/usefulFuncs';
import { TrackingEvent } from '@classes/TrackingEvent';
import InputField from '@containers/form/InputField';
import { FlatButton } from '@ui/buttons';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logError, logNetRequest } from '@utils/usefulFuncs';
import { Working } from '@ui/Working';
import { CollapseButton } from '@ui/buttons/CollapseButton';
import { useApiKeysEnabled, useCurrentUser } from '@hooks/user/useCurrentUser';
import { ApiKeysSection } from '@features/apiKeys';

const PageWrapper = styled.div``;
const BodyWrapper = styled.div`
  padding: 24px;
`;

export const FormWrap = styled('div')`
  margin-top: -16px;
  position: relative;
  padding: 16px 0;

  max-width: 420px;

  input {
    border: 1px solid ${({ theme }) => theme.themeColors.text} !important;
    border-radius: 5px !important;
  }
`;
export const ValidationWrap = styled('div')`
  color: ${({ theme }) => theme.themeColors.error} !important;
  font-size: 14px;
  line-height: 16px;
  height: 16px;
`;
const CheckWrapper = styled.div`
  svg {
    height: 36px;
    width: 36px;

    path {
      fill: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }
`;
const EmailFrequencyWrapper = styled.div`
  select {
    height: 40px;
    padding: 0 8px;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.themeColors.text};
    border-radius: 5px;
  }
`;
const MenuSettingWrapper = styled.div`
  position: relative;
  padding-top: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;
const RowWrapper = styled.div`
  cursor: pointer;
`;

const MenuSetting = ({ children, name }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleOpen = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <MenuSettingWrapper>
      <RowWrapper onClick={toggleOpen}>
        <Container vertical={8} row verticallyCenter spaceBetween>
          <Container right={16} bottom={9}>
            {name}
          </Container>
          <CollapseButton isCollapsed={isCollapsed} setCollapsed={setIsCollapsed} />
        </Container>
      </RowWrapper>

      {!isCollapsed && (
        <Container top={8} bottom={24} left={16}>
          {children}
        </Container>
      )}
    </MenuSettingWrapper>
  );
};

const ResetPassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [forgotPasswordComplete, setResetPasswordComplete] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  React.useEffect(() => {
    if (forgotPasswordComplete) {
      setTimeout(() => {
        try {
          window.location.reload();
        } catch (e) {
          logError(e);
        }
      }, 2000);
    }
  }, [forgotPasswordComplete]);
  const userPasswordComplete = () => {
    setIsSubmitting(false);
    setResetPasswordComplete(true);
  };

  const userPasswordError = (errorMessage) => {
    setIsSubmitting(false);
    setValidationMessage(errorMessage);
  };

  const validateAndSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    const passwordLength = password1 && password1.length > 7;
    const passWordsMatch = password1 === password2;

    const passValidation = passwordLength && passWordsMatch;
    if (!passValidation) {
      if (!passWordsMatch) {
        setValidationMessage('Please enter the same password twice.');
      } else if (!passwordLength) {
        setValidationMessage('Password must have at least 8 characters.');
      }
      return null;
    }

    setIsSubmitting(true);
    setValidationMessage(null);
    const form = {
      // user: {
      current_password: currentPassword,
      password: password1,
      password_confirmation: password2,
      // },
    };

    TrackingEvent.create('Request Change Password', {});
    const URL = 'v1/current_user/password';
    const { status, data, error } = await sendApiRequest('PATCH', URL, form);
    logNetRequest(status, data, error);
    handle400Statuses(status);
    if (status === 200) {
      TrackingEvent.create('Request Change Password Success', {});
      userPasswordComplete();
    } else {
      let message = data?.error || error || 'Something went wrong, please try again.';
      message = message.replace('invalid', 'incorrect');
      TrackingEvent.create('Request Change Password Failed', {
        Message: message,
      });
      userPasswordError(message);
    }
  };

  const handleKeyUp = (e) => {
    if (e.keyCode === 13) {
      validateAndSubmit(e);
    }
  };

  if (isSubmitting) {
    return (
      <Container>
        <Working />
      </Container>
    );
  }

  if (forgotPasswordComplete) {
    return (
      <Container top={48} bottom={64}>
        <Container row verticallyCenter>
          <CheckWrapper>
            <CheckCircle />
          </CheckWrapper>
          <Container left={15}>
            <Body1 bold>Changed Password Successfully</Body1>
          </Container>
        </Container>
        <Container top={8}>
          <Body5>Please login again with the new password.</Body5>
        </Container>
      </Container>
    );
  }

  return (
    <div onKeyDown={(e) => handleKeyUp(e)}>
      <FormWrap>
        <form>
          <Container left={4}>
            <Container top={24}>
              <Body5>Current Password</Body5>
            </Container>
            <Container top={8}>
              <InputField
                id="current-password-field-1"
                placeholder={'Current Password'}
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="password"
              />
            </Container>
            <Container top={8}>
              <Container bottom={8}>
                <Body5>New Password</Body5>
              </Container>
              <InputField
                id="password-field-1"
                placeholder={'New Password'}
                type="password"
                value={password1}
                onChange={(e) => setPassword1(e.target.value)}
                autoComplete="password1"
              />
            </Container>
            <Container bottom={8}>
              <Body5>Confirm New Password</Body5>
            </Container>
            <InputField
              id="password-field-2"
              placeholder={'Confirm New Password'}
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              autoComplete="password2"
            />

            <ValidationWrap>{validationMessage || null}</ValidationWrap>
            <Container top={16}>
              <FlatButton id="forgot-password-submit" fullWidth onClick={validateAndSubmit}>
                Submit
              </FlatButton>
            </Container>
          </Container>
        </form>
      </FormWrap>
    </div>
  );
};

const emailFrequencies = ['weekly', 'daily', 'never', 'default'];

const SetEmailFrequency = () => {
  const currentUser = useCurrentUser();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailFrequency, setEmailFrequency] = useState(currentUser.email_frequency_preference);
  const [formError, setFormError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);

  const onChange = (e) => {
    setIsComplete(false);
    setFormError(null);
    const value = e.target.value;
    setEmailFrequency(value);
    onSubmit(value);
  };

  const onSubmit = async (value) => {
    const form = {
      email_frequency_preference: value,
    };

    setIsSubmitting(true);
    const URL = 'v1/current_user';
    const { status, data, error } = await sendApiRequest('PATCH', URL, form);
    handle400Statuses(status);
    logNetRequest(status, data, error);

    if (status === 200) {
      TrackingEvent.create('Update Email Frequency Success', {});
      setIsComplete(true);
    } else {
      setIsComplete(false);
      setFormError('Something went wrong, please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <EmailFrequencyWrapper>
      <Container>
        <select value={emailFrequency} onChange={onChange}>
          {emailFrequencies.map((frequency) => (
            <option key={frequency} value={frequency}>
              {toCapitalCase(frequency)}
            </option>
          ))}
        </select>
      </Container>
      {formError && (
        <Container top={16}>
          <Body5>{formError}</Body5>
        </Container>
      )}
      {isSubmitting && (
        <Container top={16}>
          <Working text="Saving..." noVerticalPadding />
        </Container>
      )}
      {isComplete && (
        <Container top={28} left={2}>
          <Body5>Email frequency updated successfully</Body5>
        </Container>
      )}
    </EmailFrequencyWrapper>
  );
};

export const AccountSettings = () => {
  const navigate = useNavigate();
  const apiKeysEnabled = useApiKeysEnabled();

  const onGoBack = () => navigate(ROUTES.home);

  React.useEffect(() => {
    TrackingEvent.create('View Account Settings', {});
  }, []);

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <BodyWrapper>
          <Page width={'525px'}>
            <BackArrowButton onClick={onGoBack} />
            <Container top={40} bottom={16}>
              <Body1 bold>Account Settings</Body1>
            </Container>
            {apiKeysEnabled && (
              <MenuSetting name="API Keys">
                <ApiKeysSection />
              </MenuSetting>
            )}
            <MenuSetting name="Change Password">
              <ResetPassword />
            </MenuSetting>
            <MenuSetting name="Set Email Frequency">
              <SetEmailFrequency />
            </MenuSetting>
          </Page>
        </BodyWrapper>
      </Body>
    </PageWrapper>
  );
};
